import React from 'react';
import { Helmet } from 'react-helmet-async';
import { MapPin, Phone, Mail, Clock, Home, PhoneCall, Wifi, Zap, ArrowRight } from 'lucide-react';
import { Link } from 'react-router-dom';
import ContactForm from '../components/ContactForm';
import ContactMethods from '../components/ContactMethods';

const ContactPage: React.FC = () => {
  return (
    <div className="pt-24 pb-16">
      <Helmet>
        <title>Kontakt - Easy Vertrag | Tel: +49 179 7473491 | Völklingen</title>
        <meta name="description" content="Kontaktieren Sie Easy Vertrag in Völklingen. Tel: +49 179 7473491, E-Mail: easy90az@gmail.com. Montag-Freitag 10-20 Uhr, Samstag 10-18 Uhr. Bismarck Straße 31, 66333 Völklingen." />
        <meta name="keywords" content="Handyvertrag Völklingen, Mobilfunk Beratung Saarland, Internet Saarbrücken, Easy Vertrag Kontakt, Telefon, Adresse Völklingen" />
        <meta name="robots" content="index, follow" />
        <link rel="canonical" href="https://easy4vertrag.de/kontakt" />
        <meta property="og:title" content="Kontakt - Easy Vertrag" />
        <meta property="og:description" content="Kontaktieren Sie uns: Tel +49 179 7473491. Mo-Fr 10-20 Uhr, Sa 10-18 Uhr. Bismarck Straße 31, 66333 Völklingen." />
        <meta property="og:url" content="https://easy4vertrag.de/kontakt" />
        <script type="application/ld+json">{`
          {
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            "itemListElement": [
              {"@type": "ListItem", "position": 1, "name": "Startseite", "item": "https://easy4vertrag.de/"},
              {"@type": "ListItem", "position": 2, "name": "Kontakt", "item": "https://easy4vertrag.de/kontakt"}
            ]
          }
        `}</script>
        <script type="application/ld+json">{`
          {
            "@context": "https://schema.org",
            "@type": "FAQPage",
            "mainEntity": [
              {
                "@type": "Question",
                "name": "Wo befindet sich Easy Vertrag in Völklingen?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "Unser Büro befindet sich in der Bismarck Straße 31, 66333 Völklingen im Saarland. Sie können uns persönlich besuchen oder online und telefonisch beraten lassen."
                }
              },
              {
                "@type": "Question",
                "name": "Kann ich einen Handyvertrag in Völklingen persönlich abschließen?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "Ja! Wir beraten Sie persönlich in Völklingen zu allen Mobilfunkverträgen von Telekom, Vodafone, O2 und weiteren Anbietern. Terminvereinbarung per WhatsApp oder Telefon."
                }
              },
              {
                "@type": "Question",
                "name": "Welche Gebiete im Saarland betreut Easy Vertrag?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "Wir betreuen Kunden aus Völklingen, Saarbrücken, Saarlouis, Dillingen, Wadgassen, Quierschied, Heusweiler, Püttlingen und weiteren Gemeinden im Saarland."
                }
              },
              {
                "@type": "Question",
                "name": "Beraten Sie auch zu Internet und Strom in Völklingen?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "Ja, neben Handyverträgen bieten wir auch Vergleiche für DSL- und Glasfaser-Internettarife sowie Strom- und Gastarife in Völklingen und dem Saarland an."
                }
              },
              {
                "@type": "Question",
                "name": "Wie kann ich einen Termin vereinbaren?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "Sie können uns per WhatsApp (+49 179 7473491), per E-Mail (easy90az@gmail.com) oder telefonisch kontaktieren. Montag bis Freitag 10-20 Uhr, Samstag 10-18 Uhr."
                }
              }
            ]
          }
        `}</script>
      </Helmet>
      <section className="bg-green-600 text-white py-12">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto text-center">
            <h1 className="text-3xl md:text-4xl font-bold mb-4">Handyvertrag-Beratung in Völklingen - Kontakt</h1>
            <p className="text-xl text-blue-100">
              Beratung zu Handyverträgen, Smartphones, Internet und Energie im Saarland - persönlich, per WhatsApp oder telefonisch.
            </p>
          </div>
        </div>
      </section>

      <section className="py-16">
        <div className="container mx-auto px-4">
          <div className="grid md:grid-cols-3 gap-8 mb-12">
            <div className="bg-white p-6 rounded-lg shadow-md flex flex-col items-center text-center">
              <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center mb-4">
                <MapPin className="w-6 h-6 text-blue-600" />
              </div>
              <h3 className="text-xl font-bold text-gray-800 mb-2">Adresse</h3>
              <p className="text-gray-600">
                Bismarck Straße 31<br />
                66333 Völklingen<br />
                Deutschland
              </p>
            </div>
            <div className="bg-white p-6 rounded-lg shadow-md flex flex-col items-center text-center">
              <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center mb-4">
                <Phone className="w-6 h-6 text-blue-600" />
              </div>
              <h3 className="text-xl font-bold text-gray-800 mb-2">Telefon & E-Mail</h3>
              <p className="text-gray-600 mb-2">
                <a href="tel:+4917974734911" className="hover:text-blue-600 transition-colors">
                  +49 179 7473491
                </a>
              </p>
              <p className="text-gray-600">
                <a href="mailto:easy90az@gmail.com" className="hover:text-blue-600 transition-colors">
                  easy90az@gmail.com
                </a>
              </p>
            </div>
            <div className="bg-white p-6 rounded-lg shadow-md flex flex-col items-center text-center">
              <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center mb-4">
                <Clock className="w-6 h-6 text-blue-600" />
              </div>
              <h3 className="text-xl font-bold text-gray-800 mb-2">Öffnungszeiten</h3>
              <p className="text-gray-600">
                Montag - Freitag: 10:00 - 20:00 Uhr<br />
                Samstag: 10:00 - 18:00 Uhr<br />
                Sonntag: Geschlossen
              </p>
            </div>
          </div>

          {/* Contact Form */}
          <div className="mb-12">
            <ContactForm />
          </div>

          {/* Contact Methods */}
          <div className="mb-12">
            <ContactMethods 
              phoneNumber="+49 179 7473491"
              email="easy90az@gmail.com"
              whatsappMessage="Hallo! Ich habe eine Frage zu Ihren Angeboten."
              showWhatsApp={true}
            />
          </div>

          <div>
            <h2 className="text-2xl font-bold text-gray-800 mb-6">Unser Standort</h2>
            <div className="bg-white rounded-lg shadow-md overflow-hidden h-[450px]">
              <iframe
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d2589.924293341862!2d6.858799776268334!3d49.25338637139726!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x4795a5c6c3c8edb1%3A0x3c2f4b68f0ddb34f!2sBismarckstra%C3%9Fe%2031%2C%2066333%20V%C3%B6lklingen!5e0!3m2!1sde!2sde!4v1710927146099!5m2!1sde!2sde"
                width="100%"
                height="450"
                style={{ border: 0 }}
                allowFullScreen
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                title="Standort von Easy Vertrag"
                className="w-full h-full"
              ></iframe>
            </div>
          </div>
        </div>
      </section>

      <section className="py-12 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto">
            <h2 className="text-2xl font-bold text-gray-800 mb-2">Häufige Fragen zur Beratung in Völklingen</h2>
            <p className="text-gray-600 mb-6">
              Antworten auf die wichtigsten Fragen zu unserer Beratung und unseren Leistungen im Saarland.
            </p>

            <div className="space-y-3">
              {[
                {
                  q: 'Wo befindet sich Easy Vertrag in Völklingen?',
                  a: 'Unser Büro befindet sich in der Bismarck Straße 31, 66333 Völklingen im Saarland. Sie können uns persönlich besuchen oder online und telefonisch beraten lassen.',
                },
                {
                  q: 'Kann ich einen Handyvertrag in Völklingen persönlich abschließen?',
                  a: 'Ja! Wir beraten Sie persönlich in Völklingen zu allen Mobilfunkverträgen von Telekom, Vodafone, O2 und weiteren Anbietern. Terminvereinbarung per WhatsApp oder Telefon.',
                },
                {
                  q: 'Welche Gebiete im Saarland betreut Easy Vertrag?',
                  a: 'Wir betreuen Kunden aus Völklingen, Saarbrücken, Saarlouis, Dillingen, Wadgassen, Quierschied, Heusweiler, Püttlingen und weiteren Gemeinden im Saarland. Telefonische Beratung deutschlandweit.',
                },
                {
                  q: 'Beraten Sie auch zu Internet und Strom in Völklingen?',
                  a: 'Ja, neben Handyverträgen bieten wir auch Vergleiche für DSL- und Glasfaser-Internettarife sowie Strom- und Gastarife in Völklingen und dem Saarland an.',
                },
                {
                  q: 'Wie kann ich einen Termin vereinbaren?',
                  a: 'Sie können uns per WhatsApp (+49 179 7473491), per E-Mail (easy90az@gmail.com) oder telefonisch kontaktieren. Montag bis Freitag 10-20 Uhr, Samstag 10-18 Uhr.',
                },
              ].map((item, i, arr) => (
                <div key={i} className={`bg-white rounded-lg shadow-sm p-5 ${i < arr.length - 1 ? 'border-b border-gray-100' : ''}`}>
                  <h3 className="text-base font-semibold text-gray-800 mb-2">{item.q}</h3>
                  <p className="text-gray-600 text-sm">{item.a}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>
      {/* Navigation Buttons Section */}
      <section className="py-14 bg-blue-600">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center mb-10">
            <h2 className="text-2xl font-bold text-white mb-2">Weitere Seiten entdecken</h2>
            <p className="text-blue-100">Besuchen Sie unsere anderen Angebote und Informationsseiten.</p>
          </div>
          <div className="max-w-4xl mx-auto grid grid-cols-2 md:grid-cols-4 gap-4">
            <Link
              to="/"
              className="group bg-white rounded-xl p-5 flex flex-col items-center text-center hover:shadow-lg hover:-translate-y-1 transition-all duration-200"
            >
              <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center mb-3 group-hover:bg-blue-600 transition-colors">
                <Home className="w-5 h-5 text-blue-600 group-hover:text-white transition-colors" />
              </div>
              <span className="font-semibold text-gray-800 text-sm">Startseite</span>
            </Link>
            <Link
              to="/produkte/mobilfunk"
              className="group bg-white rounded-xl p-5 flex flex-col items-center text-center hover:shadow-lg hover:-translate-y-1 transition-all duration-200"
            >
              <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center mb-3 group-hover:bg-blue-600 transition-colors">
                <PhoneCall className="w-5 h-5 text-blue-600 group-hover:text-white transition-colors" />
              </div>
              <span className="font-semibold text-gray-800 text-sm">Mobilfunk</span>
            </Link>
            <Link
              to="/internet-zuhause"
              className="group bg-white rounded-xl p-5 flex flex-col items-center text-center hover:shadow-lg hover:-translate-y-1 transition-all duration-200"
            >
              <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center mb-3 group-hover:bg-green-600 transition-colors">
                <Wifi className="w-5 h-5 text-green-600 group-hover:text-white transition-colors" />
              </div>
              <span className="font-semibold text-gray-800 text-sm">Internet zuhause</span>
            </Link>
            <Link
              to="/strom-gas"
              className="group bg-white rounded-xl p-5 flex flex-col items-center text-center hover:shadow-lg hover:-translate-y-1 transition-all duration-200"
            >
              <div className="w-10 h-10 bg-orange-100 rounded-full flex items-center justify-center mb-3 group-hover:bg-orange-500 transition-colors">
                <Zap className="w-5 h-5 text-orange-500 group-hover:text-white transition-colors" />
              </div>
              <span className="font-semibold text-gray-800 text-sm">Strom & Gas</span>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ContactPage;